import { cn } from '@/lib/utils';

/**
 * The one progress bar in the app, and the one place the difference between
 * "no progress" and "no plan" is drawn.
 *
 * A determinate bar advances with real `progress.done / total` and transitions
 * its width, so a step landing is a movement rather than a repaint. `null` means
 * the worker is running and has reported no todo list, which is a different fact
 * from zero: it gets a faint full-width fill with a moving highlight, so even a
 * still frame reads as "running, length unknown" rather than as a bar stuck at
 * one end. That distinction is the reason this is a component and not four
 * lines inlined in three places.
 */
export function ProgressBar({
  fraction,
  className,
}: {
  fraction: number | null;
  className?: string;
}) {
  if (fraction === null) {
    return (
      <span
        className={cn('bg-raised relative block h-[3px] overflow-hidden rounded-full', className)}
        aria-hidden
      >
        <span className="bg-info/20 absolute inset-0 rounded-full" />
        <span
          className="dy-shimmer absolute inset-y-0 left-0 w-1/3 rounded-full"
          style={{ background: 'var(--dy-grad-x)' }}
        />
      </span>
    );
  }
  return (
    <span
      className={cn('bg-raised block h-[3px] overflow-hidden rounded-full', className)}
      aria-hidden
    >
      <span
        className="block h-full rounded-full transition-[width] duration-500 ease-out"
        style={{ width: `${Math.round(fraction * 100)}%`, background: 'var(--dy-grad-x)' }}
      />
    </span>
  );
}
